import { useEffect, useState, useRef } from "react";
import { saveStep } from "../../../../services/stepService";
import Button from "../../../forms/Button";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { profileDetails } from "../../../../services/ProfileService";
import { bookEdit } from "../../../../services/bookService";
import Image from "../../../elements/Image";
import { updateBook } from "../../../../redux/slice/profileSlice";
import TextareaAutoSizeWithLimit from "../../../form/TextareaAutoSizeWithLimit";
import TextAreaAutoSize from "../../../form/TextareaAutoSize";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import Artwork from "../../../../assets/images/bgartwork.webp"
import Artlogo from "../../../../assets/images/artlogo.webp"
import StepProgressView from "../../../common/StepProgressView";
import { getActiveStep } from "../../../../services/commonService";
import { markStepAsVisited } from "../../../../redux/slice/profileSlice";
import Textarea from "../../../form/Textarea";

export const CoverEditor = ({ image }) => {
  const [, setProfileDetails] = useState({});
  const [bookTitle, setBookTitle] = useState("");
  const [forewordTitle, setForewordTitle] = useState("Foreword");
  const [forewordContent, setForewordContent] = useState("");
  const { width, height } = useWindowDimensions();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  // Add refs for textareas
  const bookTitleRef = useRef(null);
  const forewordTitleRef = useRef(null);
  const forewordContentRef = useRef(null);

  const { session } = useSelector((state) => state.authProvider);
  const { myBooks } = useSelector((state) => state.profile);
  const { coverImages: reduxCoverImages } = useSelector((state) => state.sketch);
  const siteSetting = useSelector((state) => state.setting?.siteSetting);
  const { activeBookId, uploadingCoverImage } = useSelector((state) => state.stepFlow);

  const debounceTimeoutRef = useRef(null);
  const debounceForewordTitleTimeoutRef = useRef(null);
  const debounceForewordContentTimeoutRef = useRef(null);

  // Set the book title dynamically
  useEffect(() => {
    setBookTitle(getBook(activeBookId)?.title ?? null);
    setForewordTitle(getBook(activeBookId)?.introKeyword ?? null);
    setForewordContent(getBook(activeBookId)?.description ?? null);
  }, [myBooks.length, activeBookId]);

  const getBook = (bookId) => {
    return myBooks.find((book) => book._id === bookId) ?? null;
  };

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await profileDetails();
        if (res?.status === 200) {
          setProfileDetails(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch profile details:", err);
      }
    };
    fetchProfile();
  }, [dispatch]);

  // Get cover image URL from AIUrl
  const getCoverImageUrl = () => {
    const coverImage = reduxCoverImages.find((img) => img.isCover);
    return coverImage?.AIUrl || coverImage?.url || "";
  };

  // Focus handlers for pencil icons
  const handleBookTitleFocus = () => {
    if (bookTitleRef.current) {
      bookTitleRef.current.focus();
    }
  };

  const handleForewordTitleFocus = () => {
    if (forewordTitleRef.current) {
      forewordTitleRef.current.focus();
    }
  };

  const handleForewordContentFocus = () => {
    if (forewordContentRef.current) {
      forewordContentRef.current.focus();
    }
  };

  const nextStep = async () => {
    try {
      // Get cover image URL
      const imageUrl = getCoverImageUrl();

      if (!!imageUrl) {
        bookEdit({
          id: activeBookId,
          title: bookTitle,
          coverImage: imageUrl,
          introKeyword: forewordTitle,
          description: forewordContent,
        });
      }

      dispatch(updateBook({ _id: activeBookId, coverImage: imageUrl }));
      // Continue with step saving
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: "Review_Your_Book",
      });
      dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Review_Your_Book' }))
      // markStepAsVisited('Review_Your_Book', activeBookId);
      navigate(`${"/review"}?stepCode=${"Review_Your_Book"}`);
    } catch (err) {
      console.warn("Error in nextStep:", err);
    }
  };

  const hasCoverImage = getCoverImageUrl() !== "";
  const stepNumber = getActiveStep();
  return (
    <div className="w-full flex flex-col items-center">
      <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold text-black mb-2 md:mb-4 xl:mb-6 text-center flex gap-5 items-center justify-center flex-col-reverse md:flex-row">Design Your Cover Page <div className="h-6 md:h-9 flex items-center justify-center md:px-5 px-3 md:rounded-lg rounded-full border border-md-pink text-md-pink text-[10px] md:text-base">Step {stepNumber} of 5</div></h1>
      <StepProgressView number={stepNumber} />
      <div className="bg-transparent md:bg-white rounded-lg overflow-hidden py-3 px-2 md:px-6 flex items-center justify-between gap-2 lg:gap-4 mb-6 w-full">
        <div className="flex-shrink-0">
          <Button
            buttonLabel="Back"
            buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
            buttonLabelClasses={"text-sm !font-bold"}
            buttonFunction={async () => {
              saveStep({
                userId: session?.user?._id,
                bookId: activeBookId,
                isCompleted: 'yes',
                stepCode: "Upload_Images",
              });
              dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Images' }))
              // markStepAsVisited('Upload_Images', activeBookId);
              navigate(`/create?stepCode=${"Upload_Images"}`);
            }}
          />
        </div>

        <div className="flex-grow flex-shrink max-w-md text-black font-bold text-sm lg:text-base text-center hidden md:block">
          Design your cover and write your custom foreword.</div>
        <div className="flex-shrink-0">
          <Button
            buttonLabel="Next"
            buttonClasses={"!rounded-lg !h-10 !px-11 md:!px-8 !gap-0.5 !text-sm"}
            buttonLabelClasses={"text-sm !font-bold"}
            buttonFunction={nextStep}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-10">
        <div className="bg-white rounded-lg overflow-hidden relative order-2 md:order-1">
          <div className="relative py-0 px-6 h-full flex flex-col">
            <div className="absolute left-0 top-2 z-[1] w-full">
              <div className="w-56 aspect-[3/1] overflow-hidden mx-auto">
                <Image src={Artlogo} classes={"object-contain"} />
              </div>
            </div>

            <div className="flex items-center justify-between w-full relative z-[2] mt-auto mb-10">
              <TextareaAutoSizeWithLimit
                // ref={forewordTitleRef}
                inputPlaceholder={"Title"}
                inputValue={bookTitle || ""}
                inputClasses={"!text-center !text-2xl !font-semibold !bg-transparent !border-transparent !w-full !overflow-none !resize-none font-Permanentmarker !text-md-pink"}
                currentHeight={"50px"}
                minFontSize={10}
                maxFontSize={24}
                baseFontSize={24}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setBookTitle(newTitle);
                  if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                  }
                  debounceTimeoutRef.current = setTimeout(() => {
                    bookEdit({ id: activeBookId, title: newTitle });
                  }, 500);
                  dispatch(updateBook({ _id: activeBookId, title: newTitle }));
                }}
                onBlur={(e) => {
                  bookEdit({ id: activeBookId, title: bookTitle });
                  dispatch(updateBook({ _id: activeBookId, title: bookTitle }));
                }}
              />
              {/* <Textarea
                inputPlaceholder={"Title"}
                inputValue={bookTitle || ""}
                inputClasses={"!text-center !text-2xl !font-semibold !bg-transparent !border-transparent !w-full !overflow-none !resize-none font-Permanentmarker !text-md-pink"}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setBookTitle(newTitle);
                  if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                  }
                  debounceTimeoutRef.current = setTimeout(() => {
                    bookEdit({ id: activeBookId, title: newTitle });
                  }, 500);
                  dispatch(updateBook({ _id: activeBookId, title: newTitle }));
                }}
                onBlur={(e) => {
                  bookEdit({ id: activeBookId, title: bookTitle });
                  dispatch(updateBook({ _id: activeBookId, title: bookTitle }));
                }}
              /> */}
              <i className={`fa-solid fa-pen text-sm text-pink-500 cursor-pointer flex-shrink-0 ml-auto`} onClick={handleBookTitleFocus}></i>
            </div>
            <div className="w-full  h-full mx-auto overflow-hidden absolute left-0 top-0">
              {hasCoverImage ? <Image src={getCoverImageUrl()} alt={bookTitle} classes={"object-cover"} /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-500">No Cover Image</div>}

              {uploadingCoverImage && (
                <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] rounded-lg sm:rounded-2xl z-20 flex items-center justify-center">
                  <div className="bg-white/90 rounded-full p-3 shadow-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-pink-500 border-t-transparent"></div>
                  </div>
                  <div className="absolute bottom-2 left-0 right-0 text-center">
                    <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded-full">Generating...</span>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full aspect-[3/1] overflow-hidden absolute left-0 bottom-0 z-[1]">
              <Image src={Artwork} classes={"object-contain"} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg overflow-hidden order-1 md:order-2">
          <div className="relative py-10 px-10 space-y-5">
            <div className="flex items-center">

              <TextareaAutoSizeWithLimit
                ref={forewordTitleRef}
                inputPlaceholder={"Foreword"}
                inputValue={forewordTitle || ""}
                inputClasses={"!text-center !font-semibold !bg-transparent !border-transparent !w-full"}
                currentHeight={"50px"}
                maxLines={2}
                minFontSize={10}
                maxFontSize={24}
                baseFontSize={24}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setForewordTitle(newTitle);

                  // Clear existing timeout
                  if (debounceForewordTitleTimeoutRef.current) {
                    clearTimeout(debounceForewordTitleTimeoutRef.current);
                  }

                  // Set new timeout for bookEdit
                  debounceForewordTitleTimeoutRef.current = setTimeout(() => {
                    bookEdit({ id: activeBookId, introKeyword: newTitle });
                  }, 500);

                  // Dispatch immediately for UI responsiveness
                  dispatch(updateBook({ _id: activeBookId, introKeyword: newTitle }));
                }}
                onBlur={(e) => {
                  bookEdit({ id: activeBookId, introKeyword: forewordTitle });
                  dispatch(updateBook({ _id: activeBookId, introKeyword: forewordTitle }));
                }}
              />
              <i className={`fa-solid fa-pen text-sm text-pink-500 cursor-pointer ml-2 flex-shrink-0`} onClick={handleForewordTitleFocus}></i>
            </div>

            <div className="relative space-y-1">
              <Textarea
                inputPlaceholder={"Write a heartfelt message to the gift recipient here..."}
                inputValue={forewordContent || ""}
                inputClasses={"h-80 !text-base !bg-transparent !border-2 !border-gray-200 !rounded-xl !border-md-pink !p-5 md:!pl-8 !resize-none overflow-y-auto"}
                onChange={(e) => {
                  const newDesc = e.target.value;
                  setForewordContent(newDesc);

                  // Clear existing timeout
                  if (debounceForewordContentTimeoutRef.current) {
                    clearTimeout(debounceForewordContentTimeoutRef.current);
                  }

                  // Set new timeout for bookEdit
                  debounceForewordContentTimeoutRef.current = setTimeout(() => {
                    bookEdit({ id: activeBookId, description: newDesc });
                  }, 500);

                  // Dispatch immediately for UI responsiveness
                  dispatch(updateBook({ _id: activeBookId, description: newDesc }));
                }}
                onBlur={(e) => {
                  bookEdit({ id: activeBookId, description: forewordContent });
                  dispatch(updateBook({ _id: activeBookId, description: forewordContent }));
                }}
              />
              {/* <TextAreaAutoSize
                    ref={forewordContentRef}
                    inputPlaceholder={"Write a heartfelt message to the gift recipient here..."}
                    inputValue={forewordContent || ""}
                    inputClasses={"!text-base !bg-transparent !border-2 !border-gray-200 !rounded-xl !border-md-pink !p-5 md:!pl-8 !resize-none overflow-y-auto"}
                    currentHeight={320}
                    onChange={(e) => {
                      const newDesc = e.target.value;
                      setForewordContent(newDesc);

                      // Clear existing timeout
                      if (debounceForewordContentTimeoutRef.current) {
                        clearTimeout(debounceForewordContentTimeoutRef.current);
                      }

                      // Set new timeout for bookEdit
                      debounceForewordContentTimeoutRef.current = setTimeout(() => {
                        bookEdit({ id: activeBookId, description: newDesc });
                      }, 500);

                      // Dispatch immediately for UI responsiveness
                      dispatch(updateBook({ _id: activeBookId, description: newDesc }));
                    }}
                    onBlur={(e) => {
                      bookEdit({ id: activeBookId, description: forewordContent });
                      dispatch(updateBook({ _id: activeBookId, description: forewordContent }));
                    }}
                  /> */}
              <div className=" text-black text-right">{forewordContent?.length || "0"}/1500</div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};
